/* eslint-disable no-promise-executor-return */
import React from 'react';
import Cookies from 'js-cookie';
import { concat, descend, groupBy, isEmpty, mergeWith, prop, sort, sortWith, uniqBy } from 'ramda';
import moment from 'moment';
import { useOutletContext } from 'react-router-dom';
import {
  Alert,
  AppAction,
  AppDispatch,
  AppState,
  AssignedProvider,
  AuthUser,
  Note,
  Patient,
  QuestionAnswers,
} from '../types/types';
import { clearCurrentUserOnSentry, reportUserIdentityToSentry } from '../utils/reportToSentry';
import {
  AUTH_TOKEN_STORE_KEY,
  AUTH_USER_STORE_KEY,
  isAFamilyDependent,
  moveFilteredItemsToTop,
  randomTempIdGenerator,
  sessionTStorage,
  storage,
  TEMP_ADVANCED_PATIENTS_SEARCHES_STORE_KEY,
} from '../utils/utils';
import { paths } from '../routes';
import { deregisterNotification, getPatients, getQuestionnaires } from '../api/api';
import readAWSS3File from '../utils/readAWSS3File';
import { VISIT_STATUS, visitTypes } from './visitContext';

const initialState: AppState = {
  user: null,
  patients: [],
  alert: { message: '', type: 'INFO', show: false },
  modalState: 'CLOSED',
  unReadMessages: undefined,
};

const AppContext = React.createContext<[state: AppState, dispatch: AppDispatch] | undefined>(
  undefined,
);

AppContext.displayName = 'AppContext';

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_AUTH_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'SET_ASSIGNED_PATIENTS':
      return {
        ...state,
        patients: action.payload,
      };
    case 'SET_UNREAD_MESSAGES':
      return {
        ...state,
        unReadMessages: action.payload,
      };
    case 'SHOW_ALERT':
      return {
        ...state,
        alert: action.payload,
      };
    case 'SET_MODAL_STATE':
      return {
        ...state,
        modalState: action.payload,
      };
    default:
      return state;
  }
}

function AppProvider({ children }: { children: React.ReactNode }) {
  const [isHardLoading, setHardLoading] = React.useState(true);
  const [state, dispatch] = React.useReducer(appReducer, initialState);

  const value = React.useMemo(() => [state, dispatch], [state, dispatch]) as [
    state: AppState,
    dispatch: AppDispatch,
  ];

  React.useEffect(() => {
    (async function fakeHardLoadRequest() {
      return new Promise(resolve => setTimeout(() => resolve(null), 2000));
    })().then(() => {
      const el = document.querySelector('.pre-loader');
      if (el) {
        el.remove();
        setHardLoading(fndIsHardLoading => !fndIsHardLoading);
      }
    });
  }, [isHardLoading]);

  if (isHardLoading) {
    return null;
  }

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

function useAppContext() {
  const context = React.useContext(AppContext);
  if (context === undefined) {
    throw new Error(`\`useAppContext\` must be used within a AppProvider`);
  }
  return context;
}

function usePatient() {
  return useOutletContext<{ patient: Patient }>();
}

export function showAlert(
  dispatch: AppDispatch,
  { show, type, message } = {} as Alert,
  autoDismissTimer = 0,
) {
  dispatch({
    type: 'SHOW_ALERT',
    payload: { show, type, message },
  });

  if (autoDismissTimer) {
    setTimeout(
      () =>
        dispatch({
          type: 'SHOW_ALERT',
          payload: { show: false, type: 'INFO', message: '' },
        }),
      autoDismissTimer,
    );
  }
}

export async function logout(user?: AuthUser | null) {
  Cookies.remove(AUTH_TOKEN_STORE_KEY);
  storage.remove(AUTH_USER_STORE_KEY);
  sessionTStorage.remove(TEMP_ADVANCED_PATIENTS_SEARCHES_STORE_KEY);
  window.Intercom('shutdown');
  clearCurrentUserOnSentry();

  if (user && user?.pushNotifications) {
    await Promise.all(
      user?.pushNotifications.map(noti => {
        return deregisterNotification({
          app: 'GUIDE',
          deviceToken: noti.deviceToken,
          oneSignalToken: noti.oneSignalToken,
        });
      }),
    );
  }

  window.location.replace(paths.LOGIN_URL_PATH);
}

export async function getAssignedPatients(
  user: AuthUser | null,
  params?: Record<string, string>,
): Promise<Patient[]> {
  const patients = await getPatients('/user/patients', params);

  let assignedPatients = (patients || [])
    .map((patient: Patient) => {
      const questionnaires = sortWith(
        [descend(prop('createdAt') as any)],
        (patient?.patientDetails as any)?.questionnaires || [],
      ).filter(
        (fndQuestionnaire: any) =>
          Boolean(fndQuestionnaire.answers.length) &&
          Boolean(visitTypes.find(fndVisitType => fndVisitType.type === fndQuestionnaire.type)) &&
          (fndQuestionnaire.status === VISIT_STATUS.COMPLETE ||
            (!params &&
              fndQuestionnaire.status === VISIT_STATUS.UNANSWERED &&
              fndQuestionnaire.providerUserId === user?.id)),
      ) as QuestionAnswers[];

      return {
        ...patient,
        signedAvatar: '',
        questionnaires,
        assignedProvider: getAssignedProvider(patient),
      };
    })
    .filter(
      (patient: Patient) =>
        patient.id !== user?.id &&
        !isEmpty(patient.firstName) &&
        !isEmpty(patient.lastName) &&
        (!isEmpty(patient.email) || isAFamilyDependent(patient)),
    );

  const tempPatientSearch = JSON.parse(
    sessionTStorage.get(TEMP_ADVANCED_PATIENTS_SEARCHES_STORE_KEY) || '{}',
  );

  if (Object.keys(tempPatientSearch).length > 0 && !params) {
    assignedPatients = uniqBy(prop('phoneNumber'), [...assignedPatients, tempPatientSearch]);
  } else {
    assignedPatients = uniqBy(prop('phoneNumber'), assignedPatients);
  }

  return assignedPatients;
}

export async function getPatientsQuestionnaires(
  patients: Patient[],
  userId?: string,
): Promise<[Patient[], Array<[string, Patient[]]>]> {
  if (!userId) return [patients, sortPatientsVisitsByMonthYear(patients)];

  const patientsWithVisits = (await Promise.all(
    patients.map(async (patient: Patient) => {
      const pVisits = (
        await getQuestionnaires({
          patientId: patient.id,
          status: VISIT_STATUS.UNANSWERED,
        })
      ).filter(
        (fndQuestionnaire: QuestionAnswers) =>
          fndQuestionnaire.status === VISIT_STATUS.UNANSWERED &&
          fndQuestionnaire.providerUserId === userId,
      );

      const visits = [
        ...pVisits,
        ...patient.questionnaires.filter(
          (fndQuestionnaire: QuestionAnswers) => fndQuestionnaire.status === VISIT_STATUS.COMPLETE,
        ),
      ];

      return { ...patient, questionnaires: visits };
    }),
  )) satisfies Patient[];

  return [patientsWithVisits, sortPatientsVisitsByMonthYear(patientsWithVisits)];
}

export async function loadPatientAvatar(picture: string): Promise<string> {
  return readAWSS3File('avatar', picture);
}

export async function getNotes({
  sortType,
  patient,
}: {
  sortType: 'ALL' | 'PRIVATE' | 'PRIVATE_ADMIN';
  patient: Patient;
}): Promise<Note[]> {
  let allExtendedNotes = await Promise.all(
    (patient?.patientDetails?.extendedNotes || []).map(async note => {
      return {
        ...note,
        id: randomTempIdGenerator(),
        ...(note.author && {
          author: {
            ...note.author,
            signedAvatar: await readAWSS3File('avatar', note.author.profilePicture),
          },
        }),
        ...(note.attachment && {
          attachment: await readAWSS3File('document', note.attachment),
        }),
      };
    }),
  );

  if (sortType === 'PRIVATE') {
    allExtendedNotes = allExtendedNotes.filter((note: Note) => note.isPrivate);
  }

  if (sortType === 'PRIVATE_ADMIN') {
    allExtendedNotes = patient.description
      ? [
          {
            id: randomTempIdGenerator(),
            text: patient.description,
            date: '',
            authorId: '',
            author: {
              id: '',
              firstName: 'Patient Services',
              lastName: 'Team',
              email: '',
              phoneNumber: '',
              profilePicture: '',
              signedAvatar: '',
            },
            attachment: '',
            isPrivate: false,
          },
        ]
      : [];
  }

  allExtendedNotes = sort((a: any, b: any) => {
    const newA = moment(a.date);
    const newB = moment(b.date);
    return newB.diff(newA);
  }, allExtendedNotes).filter(fndNote => fndNote.author);

  return allExtendedNotes;
}

export function sortPatientsVisitsByMonthYear(patients: Patient[]): Array<[string, Patient[]]> {
  const patientsWithNewVisits = patients.filter(
    fndPatient => fndPatient.questionnaires?.[0]?.status === VISIT_STATUS.UNANSWERED,
  );

  const newVisitMonth = moment(
    patientsWithNewVisits[0]?.questionnaires?.[0]?.createdAt,
    'YYYY-MM-DD',
  ).format('MMMM YYYY');

  const groupPatientsWithoutNewVisits = groupPatientsByMonth(
    patients.filter(
      fndPatient => fndPatient.questionnaires?.[0]?.status !== VISIT_STATUS.UNANSWERED,
    ),
  );

  let patientsByMonthYear: Array<[string, Patient[]]> = [];

  if (
    (groupPatientsWithoutNewVisits[newVisitMonth] && newVisitMonth !== 'Invalid date') ||
    (patientsWithNewVisits.length && Object.keys(groupPatientsWithoutNewVisits).length === 0)
  ) {
    const mergeVisits = mergeWith(
      concat,
      { [newVisitMonth]: patientsWithNewVisits },
      groupPatientsWithoutNewVisits,
    );

    patientsByMonthYear = moveFilteredItemsToTop({
      array: Object.entries(mergeVisits),
      findFunction: item => item[0] !== 'Invalid date',
    });

    patientsByMonthYear = sort((a: any, b: any) => {
      const newA = moment(a[0]);
      const newB = moment(b[0]);
      return newB.diff(newA);
    }, patientsByMonthYear).filter(fndGroup => fndGroup[1] && fndGroup[1].length > 0);
  } else {
    patientsByMonthYear = moveFilteredItemsToTop({
      array: Object.entries(groupPatientsWithoutNewVisits),
      findFunction: item => item[0] !== 'Invalid date',
    });

    patientsByMonthYear = sort((a: any, b: any) => {
      const newA = moment(a[0]);
      const newB = moment(b[0]);
      return newB.diff(newA);
    }, patientsByMonthYear)
      .filter(fndGroup => fndGroup[1] && fndGroup[1].length > 0)
      .map((fndGroup, index) => {
        if (index !== 0) {
          return fndGroup;
        }
        return [fndGroup[0], [...patientsWithNewVisits, ...fndGroup[1]]];
      });
  }

  return patientsByMonthYear;
}

export function getAssignedProvider(patient: Patient): AssignedProvider | null {
  let provider = null;

  const guides = (patient?.patientDetails as any)?.guides;

  if (!guides || guides?.length === 0) {
    return provider;
  }

  provider = {
    id: guides[0]?.id,
    firstName: guides[0]?.firstName,
    lastName: guides[0]?.lastName,
    email: guides[0]?.email,
    phoneNumber: guides[0]?.phoneNumber,
    profilePicture: guides[0]?.profilePicture,
    description: guides[0]?.description,
    zipCode: guides[0]?.zipCode,
    roles: undefined,
    createdAt: guides[0]?.createdAt,
    updatedAt: guides[0]?.updatedAt,
  } as AssignedProvider;

  return provider;
}

export const groupPatientsByMonth = groupBy((patient: Patient) => {
  const visit = patient.questionnaires[0];
  return moment(visit?.createdAt, 'YYYY-MM-DD').format('MMMM YYYY');
});

export function subscribeToExternals(authUser: AuthUser) {
  reportUserIdentityToSentry({ id: authUser.id });
}

export { AppProvider, useAppContext, usePatient };
